// @ts-nocheck
import { makeAutoObservable } from 'mobx';
import RootStore from '../RootStore';

declare const navigator: Navigator;
declare const window: Window;

interface MediaDeviceInfo {
  deviceId: string;
  kind: string;
  label: string;
  groupId: string;
}

interface MediaStream {
  getTracks: () => MediaStreamTrack[];
}

interface MediaStreamTrack {
  stop: () => void;
}

interface NavigatorMediaDevices {
  enumerateDevices: () => Promise<MediaDeviceInfo[]>;
  getUserMedia: (constraints: MediaConstraints) => Promise<MediaStream>;
}

interface Navigator {
  mediaDevices: NavigatorMediaDevices;
}

interface Canvas {
  width: number;
  height: number;
}

interface MediaConstraints {
  video: {
    mandatory?: {
      sourceId?: string | null;
    };
    facingMode?: string;
  };
  audio?: boolean;
}

interface Webcam extends HTMLVideoElement {
  setAttribute: (key: string, value: string) => void;
  removeAttribute: (key: string) => void;
  addEventListener: (
    key: string,
    value: (ev: any) => void,
    bool: boolean
  ) => void;
  srcObject: any;
}

interface MediaStreamError extends Error {
  name: string;
  message: string;
  constraintName?: string;
}

interface BarcodeDetectorPoint {
  x: number;
  y: number;
}
interface BarcodeDetectorBox {
  x: number;
  y: number;
  width: number;
  height: number;
}
interface BarcodeDetectorResult {
  rawValue: string;
  cornerPoints?: BarcodeDetectorPoint[];
  boundingBox?: BarcodeDetectorBox;
}
interface BarcodeDetector {
  detect: (video: HTMLVideoElement) => Promise<BarcodeDetectorResult[]>;
}

/**
 * One detected QR code from a single scan tick. `boundingBox` is in the
 * source video's NATIVE pixel coordinates (use videoWidth/videoHeight to
 * scale into the rendered element).
 */
export interface DetectedCode {
  rawValue: string;
  boundingBox: BarcodeDetectorBox;
  cornerPoints?: BarcodeDetectorPoint[];
}

class BarcodeCameraStore {
  active = false;
  webcam: Webcam | null = null;
  canvas: Canvas | null = null;
  ctx = null;
  decoder: Worker | null = null;
  streaming = false;

  frontCamera = false;
  cameraEnabled = false;
  qrResult = '';

  /**
   * When true, the detect loop populates `detectedCodes` every tick but does
   * NOT auto-commit a result via `setNewQrResult`. Caller must invoke
   * `confirmActiveScan()` to actually scan.
   * When false (legacy default), the first detected code is auto-committed.
   */
  manualConfirmMode = false;
  /** All QR codes detected in the current camera frame. */
  detectedCodes: DetectedCode[] = [];
  /** Index into `detectedCodes` of the code closest to the center of frame. */
  activeCodeIndex = -1;
  /** Video frame dimensions captured at scan time — used by the overlay. */
  videoFrameWidth = 0;
  videoFrameHeight = 0;

  iOS = false;
  isMediaStreamAPISupported = false;
  barcodeApi = false;

  debugMode = false;
  rootStore: RootStore;
  options;

  cameraError = {};

  constructor(rootStore: RootStore, options: any) {
    // BarcodeCameraStore constructor called
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
    this.options = options;
    this.init();
  }

  init = () => {
    if (this.debugMode) console.log('init called');
    this.iOS = ['iPad', 'iPhone', 'iPod'].indexOf(navigator.platform) >= 0;
    this.isMediaStreamAPISupported =
      navigator &&
      navigator.mediaDevices &&
      'enumerateDevices' in navigator.mediaDevices;
    this.barcodeApi = 'BarcodeDetector' in window;

    // Device and API support detection complete
  };

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  setCameraError = (err: any) => {
    this.cameraError = err;
  };

  setCanvasProperties = () => {
    if (this.debugMode) console.log('setCanvasProperties called');
    if (!this.canvas) return;
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  };

  switchCamera = () => {
    if (this.debugMode) console.log('switchCamera called');
    if (this.debugMode)
      console.log('switchCamera this.cameraEnabled', this.cameraEnabled);
    if (this.debugMode)
      console.log('switchCamera this.frontCamera', this.frontCamera);
    const cameraWasEnabled = this.cameraEnabled;
    if (cameraWasEnabled) {
      console.log('switchCamera cameraWasEnabled calling endSession');
      this.setCameraEnabled(false);
    }
    this.frontCamera = !this.frontCamera;
    if (cameraWasEnabled) {
      console.log('switchCamera cameraWasEnabled calling enableCamera');
      setTimeout(() => {
        this.setCameraEnabled(true);
      }, 1000);
    }
  };

  startCapture = (constraints: MediaConstraints) => {
    if (this.debugMode) console.log('startCapture called');
    if (this.debugMode) console.log('startCapture constraints', constraints);

    if (this.webcam) {
      this.webcam.setAttribute('playsinline', 'true');
      this.webcam.setAttribute('controls', 'true');
      setTimeout(() => {
        this.webcam?.removeAttribute('controls');
      }, 10);
    }
  };

  getWorkerURL = (url: string) => {
    const content = `importScripts( "${url}" );`;
    return URL.createObjectURL(
      new Blob([content], { type: 'text/javascript' })
    );
  };

  setPlayer = () => {
    if (this.debugMode) console.log('setPlayer called');
    const player = document.getElementById(
      'compass-barcode-player'
    ) as Webcam;
    this.webcam = player;

    if (this.isMediaStreamAPISupported) {
      if (this.debugMode)
        console.log('setPlayer isMediaStreamAPISupported true');
      // Resize webcam according to input
      if (this.webcam) {
        this.webcam.addEventListener(
          'play',
          (ev) => {
            if (!this.streaming) {
              this.setCanvasProperties();
              this.streaming = true;
            }
          },
          false
        );
      }
    } else {
      this.setCanvasProperties();
    }

    if (!this.barcodeApi) {
      console.log('no barcode api');
      const workerUrl = this.getWorkerURL(`${window.location.origin}/decoder.js`);
      this.decoder = new Worker(workerUrl);
    }
  };

  setPlayerStream = (stream: MediaStream) => {
    if (this.debugMode) console.log('setPlayerStream called');
    this.webcam!.srcObject = stream;

    if (this.isMediaStreamAPISupported) {
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices: MediaDeviceInfo[]) => {
          let device = devices.filter((device: MediaDeviceInfo) => {
            if (device.kind === 'videoinput') {
              return device;
            }
            return false;
          });

          let constraints: MediaConstraints;
          if (device.length > 1) {
            if (this.debugMode) console.log('device', device);
            constraints = {
              video: {
                mandatory: {
                  sourceId: device[device.length - 1].deviceId
                    ? device[device.length - 1].deviceId
                    : null,
                },
              },
              audio: false,
            };

            if (this.iOS) {
              constraints.video.facingMode = 'environment';
            }
            this.startCapture(constraints);
          } else if (device.length) {
            constraints = {
              video: {
                mandatory: {
                  sourceId: device[0].deviceId ? device[0].deviceId : null,
                },
              },
              audio: false,
            };

            if (this.iOS) {
              constraints.video.facingMode = 'environment';
            }

            if (!constraints.video.mandatory!.sourceId && !this.iOS) {
              this.startCapture({ video: true });
            } else {
              this.startCapture(constraints);
            }
          } else {
            this.startCapture({ video: true });
          }
        })
        .catch((err: MediaStreamError) => {
          console.error('Error occurred : ', err);
          this.setCameraError(err);
        });
    }
  };

  setCanvas = () => {
    if (this.debugMode) console.log('setCanvas called');
    const canvas = document.getElementById('compass-barcode-canvas');
    this.canvas = canvas;
    if (!canvas) {
      console.warn('BarcodeCameraStore: canvas element not found');
      return;
    }
    this.ctx = canvas.getContext('2d', { willReadFrequently: true });
  };

  setCameraEnabled = (value) => {
    if (this.debugMode) console.log('setCameraEnabled called');
    if (this.debugMode) console.log('setCameraEnabled value', value);
    this.cameraEnabled = value;

    if (this.cameraEnabled) {
      if (this.debugMode)
        console.log('setCameraEnabled turned on, called enableCamera');
      this.enableCamera();
    } else {
      if (this.debugMode)
        console.log('setCameraEnabled turned off, calling endSession');
      this.endSession();
    }
  };

  enableCamera = () => {
    if (this.debugMode) console.log('enableCamera called');

    this.setPlayer();
    this.setCanvas();
    if (this.debugMode) console.log('webcam', this.webcam);
    if (this.debugMode) console.log('canvas', this.canvas);

    navigator.mediaDevices
      .getUserMedia(this.constraints)
      .then((stream: MediaStream) => {
        this.setPlayerStream(stream);
        if (this.debugMode) console.log('webcam', this.webcam);
        if (this.debugMode)
          console.log('webcam srcObject', this.webcam?.srcObject);
        if (!this.barcodeApi) {
          if (this.debugMode) console.log('no barcode api');
          try {
            setTimeout(() => {
              // setCameraOverlay();
              if (this.isMediaStreamAPISupported) {
                if (this.debugMode) console.log('calling scan');
                try {
                  this.scan((result: string) => {
                    if (result) {
                      console.log('result', result);
                      this.setNewQrResult(result);

                      this.setCameraEnabled(false);
                    }
                  });
                } catch (err) {
                  console.log('scan err', err);
                  this.setCameraError(err);
                }
              }
            }, 1000);
          } catch (err) {
            console.warn('Barcode Camera err', err);
            this.setCameraError(err);
          }
        } else {
          const barcodeDetector = new (window as any).BarcodeDetector({
            formats: ['qr_code'],
          }) as BarcodeDetector;
          this.intervalDispose = setInterval(async () => {
            const detectedQr = await barcodeDetector.detect(
              this.webcam as HTMLVideoElement
            );

            if (this.debugMode) console.log('detectedQr', detectedQr);

            // Normalize and stash the full detection list so the UI can
            // outline every code in view. We do this in both modes so the
            // overlay still works when `manualConfirmMode` is off.
            this.setDetectedCodesFromDetector(detectedQr);

            if (detectedQr.length <= 0) {
              // Camera sees no codes — clear the list if anything was there.
              return;
            }

            if (this.manualConfirmMode) {
              // Manual confirm: outline-only — wait for user to press
              // "Scan This Code".
              console.log(
                '[BarcodeCameraStore] manual mode: detected',
                detectedQr.length,
                'code(s) — awaiting user confirm'
              );
              return;
            }

            // Auto-commit (callers that haven't opted into manual confirm).
            const result = detectedQr[0].rawValue;
            this.setNewQrResult(result);
          }, 1000);
        }
      })
      .catch((err: MediaStreamError) => {
        console.warn('getUserMedia Error', err);
        this.setCameraError(err);
        // if (err?.message === 'Permission denied') {
        //   this.rootStore.notificationStore.addNotification({
        //     message: `Camera Permission Denied`,
        //     options: { variant: 'Error' }
        //   });
        // }
        this.setCameraEnabled(false);
      });
  };

  scan = () => {
    if (this.debugMode) console.log('scan called');
    this.active = true;
    if (this.debugMode) console.log('this.active', this.active);
    // this.setCanvas();
    if (this.debugMode) console.log('this.canvas', this.canvas);
    if (this.debugMode) console.log('this.ctx', this.ctx);
    if (this.debugMode) console.log('this.decoder', this.decoder);

    // Start QR-decoder

    if (!this.decoder) {
      return console.warn('Decoder was not setup yet');
    }
    this.decoder.onmessage = (event) => this.onDecoderMessage(event);
    if (this.debugMode) console.log('this.decoder', this.decoder);

    this.newDecoderFrame();
  };

  newDecoderFrame = () => {
    if (this.debugMode) console.log('newDecoderFrame called');
    if (this.debugMode) console.log('this.active', this.active);
    if (!this.active) return;
    try {
      if (this.debugMode) console.log('this.ctx', this.ctx);
      this.ctx.drawImage(
        this.webcam,
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      if (this.debugMode) console.log('this.ctx drawImage', this.ctx);
      const imgData = this.ctx.getImageData(
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      if (this.debugMode) console.log('this.ctx getImageData', this.ctx);

      if (imgData.data) {
        this.decoder.postMessage(imgData);
      }
    } catch (e) {
      // Try-Catch to circumvent Firefox Bug #879717
      if (e.name === 'NS_ERROR_NOT_AVAILABLE') {
        setTimeout(this.newDecoderFrame, 0);
      } else {
        this.setCameraError(err);
      }
    }
  };

  clearCanvas = () => {
    if (this.debugMode) console.log('clearCanvas called');
    if (this.debugMode) console.log('clearCanvas ctx', this.ctx);
    if (this.debugMode) console.log('clearCanvas canvas', this.canvas);
    if (this.ctx) {
      this.ctx.fillStyle = 'rgba(0,0,0,0)';
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  };

  onDecoderMessage = (event) => {
    if (this.debugMode) console.log('onDecoderMessage called');
    if (this.debugMode) console.log('onDecoderMessage event', event);
    if (event.data.length > 0) {
      const newQrResult = event.data[0][2];
      if (this.debugMode) console.log('newQrResult', newQrResult);
      if (this.manualConfirmMode) {
        // Manual-confirm: the legacy worker decoder has no bounding-box
        // info, so we stage the latest detected value (no on-screen outline
        // possible) and keep scanning. The user's "Scan This Code" button
        // calls confirmActiveScan() which reads detectedCodes[0].
        this.detectedCodes = [
          {
            rawValue: newQrResult,
            boundingBox: { x: 0, y: 0, width: 1, height: 1 },
          },
        ];
        this.activeCodeIndex = 0;
        setTimeout(this.newDecoderFrame, 0);
        return;
      }
      this.active = false;
      this.setNewQrResult(newQrResult);
      setTimeout(this.endSession, 100);
    }
    setTimeout(this.newDecoderFrame, 0);
  };

  setNewQrResult = (value) => {
    if (this.debugMode) console.log('setNewQrResult called');
    if (this.debugMode) console.log('setNewQrResult value', value);
    this.qrResult = value;
  };

  endSession = () => {
    if (this.debugMode) console.log('endSession called');
    if (this.decoder) {
      if (this.debugMode) console.log('decoder defined, terminating');
      this.decoder.terminate();
    }
    this.clearCanvas();

    if (this.webcam?.srcObject) {
      this.webcam.srcObject.getTracks().forEach((track) => {
        if (this.debugMode) console.log('calling track stop', track);
        track.stop();
        if (this.debugMode) console.log('after track stop', track);
      });
    }

    this.active = false;
    this.webcam = null;
    this.canvas = null;
    this.ctx = null;
    this.decoder = null;
    this.streaming = false;

    this.cameraEnabled = false;

    return 'Session Ended';
  };

  get constraints() {
    return {
      video: {
        facingMode: this.frontCamera ? 'user' : 'environment',
      },
    };
  }

  // ---- Manual-confirm scanning ----------------------------------------

  setManualConfirmMode = (value: boolean) => {
    this.manualConfirmMode = value;
    if (!value) {
      // Leaving manual mode — clear staged detections so they don't render
      // stale overlays elsewhere.
      this.detectedCodes = [];
      this.activeCodeIndex = -1;
    }
  };

  /**
   * Normalize raw detector results into the store's DetectedCode shape, pick
   * the "active" code (closest to the center of the camera frame), and stash
   * the current video frame size for the overlay.
   */
  setDetectedCodesFromDetector = (raw: BarcodeDetectorResult[]) => {
    const video = this.webcam as HTMLVideoElement | null;
    this.videoFrameWidth = video?.videoWidth ?? 0;
    this.videoFrameHeight = video?.videoHeight ?? 0;

    const codes: DetectedCode[] = [];
    for (const r of raw) {
      // Some browsers omit boundingBox but give cornerPoints; derive a box.
      let box = r.boundingBox;
      if (!box && r.cornerPoints && r.cornerPoints.length >= 4) {
        const xs = r.cornerPoints.map((p) => p.x);
        const ys = r.cornerPoints.map((p) => p.y);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        box = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
      }
      if (!box) continue;
      codes.push({
        rawValue: r.rawValue,
        boundingBox: box,
        cornerPoints: r.cornerPoints,
      });
    }
    this.detectedCodes = codes;

    if (codes.length === 0) {
      this.activeCodeIndex = -1;
      return;
    }
    // Active = code whose center is closest to the center of the frame.
    const cx = this.videoFrameWidth / 2;
    const cy = this.videoFrameHeight / 2;
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < codes.length; i++) {
      const b = codes[i].boundingBox;
      const dx = b.x + b.width / 2 - cx;
      const dy = b.y + b.height / 2 - cy;
      const d = dx * dx + dy * dy;
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    this.activeCodeIndex = bestIdx;
  };

  /** Commit the currently-active detected code as the scan result. */
  confirmActiveScan = () => {
    if (
      this.activeCodeIndex < 0 ||
      this.activeCodeIndex >= this.detectedCodes.length
    ) {
      return false;
    }
    const code = this.detectedCodes[this.activeCodeIndex];
    if (!code) return false;
    this.setNewQrResult(code.rawValue);
    return true;
  };
}

export default BarcodeCameraStore;
